// Filtered and sorted room-player queries.

import { useMemo } from 'react';
import { useRoomPlayers } from './useRoomPlayers';
import type { RoomPlayer } from '@airhead-games/shared/types';

interface PlayerSelectionOptions {
  property?: keyof RoomPlayer;
  sort?: 'asc' | 'desc';
  sortField?: keyof RoomPlayer;
  filterInclude?: RoomPlayer[keyof RoomPlayer][];
  filterExclude?: RoomPlayer[keyof RoomPlayer][];
  filterFrom?: keyof RoomPlayer;
}

export type SelectPlayerData = {
  <T extends keyof RoomPlayer>(
    options: PlayerSelectionOptions & { property: T }
  ): RoomPlayer[T][];
  (options?: PlayerSelectionOptions & { property?: undefined }): RoomPlayer[];
};

interface UseRoomPlayerSelectionReturn {
  selectPlayerData: SelectPlayerData;
}

export function useRoomPlayerSelection(): UseRoomPlayerSelectionReturn {
  const { players } = useRoomPlayers();

  const selectPlayerData = useMemo<SelectPlayerData>(() => {
    function select<T extends keyof RoomPlayer>(
      options: PlayerSelectionOptions & { property: T }
    ): RoomPlayer[T][];
    function select(
      options?: PlayerSelectionOptions & { property?: undefined }
    ): RoomPlayer[];
    function select(
      options: PlayerSelectionOptions = {}
    ): RoomPlayer[] | RoomPlayer[keyof RoomPlayer][] {
      let filteredPlayers = players;

      const fieldToFilter = options?.filterFrom;
      if (fieldToFilter && (options?.filterInclude || options?.filterExclude)) {
        if (options?.filterInclude && options.filterInclude.length > 0) {
          if (options?.filterExclude && options.filterExclude.length > 0) {
            console.warn(
              'selectPlayerData: Both filterInclude and filterExclude specified. Using filterInclude only.'
            );
          }

          filteredPlayers = filteredPlayers.filter((player) => {
            const fieldValue = player[fieldToFilter];
            return options.filterInclude?.includes(fieldValue) ?? false;
          });
        } else if (options?.filterExclude && options.filterExclude.length > 0) {
          filteredPlayers = filteredPlayers.filter((player) => {
            const fieldValue = player[fieldToFilter];
            return !(options.filterExclude?.includes(fieldValue) ?? false);
          });
        }
      }

      const { sort, sortField } = options;
      if (sort && sortField) {
        filteredPlayers = [...filteredPlayers].sort((a, b) => {
          const aValue = a[sortField];
          const bValue = b[sortField];

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sort === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sort === 'asc' ? aValue - bValue : bValue - aValue;
          }

          const aStr = String(aValue);
          const bStr = String(bValue);
          return sort === 'asc'
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        });
      }

      const { property } = options;
      if (property === undefined) {
        return filteredPlayers;
      }

      return filteredPlayers.map((player) => player[property]);
    }

    return select;
  }, [players]);

  return { selectPlayerData };
}
